// src/context/AuthContext.jsx
import { createContext, useContext, useEffect, useState } from 'react';
import api from '../api/axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        setAuthChecked(true);
        return;
      }

      const response = await api.get('/users/profile');

      setUser(response.data);
    } catch (err) {
      console.error('Error de autenticación:', err);
      localStorage.removeItem('token');
      setError(err.response?.data?.message || 'Tu sesión ha expirado');
    } finally {
      setLoading(false);
      setAuthChecked(true);
    }
  };

  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/users/login', credentials);
      const { token, user: userData } = response.data;

      localStorage.setItem('token', token);
      setUser(userData);
      
      return { 
        success: true, 
        user: userData,
        isAdmin: userData.role === 'admin'
      };
    } catch (err) {
      console.error('Error en login:', err);
      
      let errorMessage = 'Credenciales incorrectas';
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = 'Email o contraseña incorrectos';
        } else if (err.response.status === 500) {
          errorMessage = 'Error en el servidor. Intente más tarde.';
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.message === 'Network Error') {
        errorMessage = 'No se puede conectar al servidor';
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post('/users/register', userData);
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setUser(user);
      return { success: true, user, isAdmin: user.role === 'admin' };
    } catch (err) {
      const message = err.response?.data?.message || 'No fue posible crear la cuenta';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    return true;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      error, 
      login, 
      register,
      logout, 
      checkAuth,
      authChecked,
      isAdmin: user?.role === 'admin'
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};
